import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { Country, City } from "country-state-city";
import FooterTwo from "../components/footer/FooterTwo";
import Breadcrumb from "./Breadcrumb";
import EmersonHeader from "../components/header/EmersonHeader";
import { WorldMap } from "../components/WorldMap";
import { useContactMutation } from "../services/contactService";

// Create validation schema function that takes country code
const createValidationSchema = (countryCode) => {
  return Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Name is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    company: Yup.string().required("Company name is required"),
    mobile: Yup.string()
      .required("Phone number is required")
      .test(
        "phone-format",
        "Phone number can only contain digits, spaces, dashes, plus, and parentheses",
        function (value) {
          if (!value) return true;
          // Basic format check - only digits, spaces, dashes, plus, parentheses
          return /^[\d\s\-+()]+$/.test(value);
        }
      ),
    country: Yup.string().required("Country is required"),
    city: Yup.string().required("City is required"),
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  });
};

// Initial form values
const initialValues = {
  name: "",
  email: "",
  company: "",
  mobile: "",
  country: "",
  countryCode: "",
  city: "",
  message: "",
};

// Custom Select styles for react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "56px",
    borderColor: state.isFocused
      ? "#ef1414"
      : state.hasValue && state.selectProps.error
      ? "#ff0000"
      : "#e0e0e0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(239, 20, 20, 0.1)" : "none",
    borderRadius: "8px",
    padding: "0 4px",
    backgroundColor: state.isDisabled ? "#f5f5f5" : "#fff",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#ef1414" : "#ccc",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#999",
    fontSize: "16px",
  }),
  input: (base) => ({
    ...base,
    fontSize: "16px",
    padding: "0",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "16px",
    color: "#333",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    marginTop: "4px",
  }),
  option: (base, state) => ({
    ...base,
    padding: "12px 16px",
    fontSize: "16px",
    backgroundColor: state.isSelected
      ? "#ef1414"
      : state.isFocused
      ? "#fff5f5"
      : "#fff",
    color: state.isSelected ? "#fff" : "#333",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:active": {
      backgroundColor: "#ef1414",
    },
  }),
};

function ContactUs() {
  const [submitMessage, setSubmitMessage] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const formActionsRef = useRef({ resetForm: null, setSubmitting: null });

  // Memoize countries list - only compute once
  const countryOptions = useMemo(() => {
    return Country.getAllCountries().map((country) => ({
      value: country.name,
      label: `${country.name} (+${country.phonecode})`,
      isoCode: country.isoCode,
      phonecode: country.phonecode,
    }));
  }, []);

  // Get cities for selected country - memoized
  const cityOptions = useMemo(() => {
    if (!selectedCountryCode) return [];
    const cities = City.getCitiesOfCountry(selectedCountryCode);
    if (!cities) return [];
    return cities.map((city) => ({
      value: city.name,
      label: city.name,
    }));
  }, [selectedCountryCode]);

  const { mutate: submitContact, isPending } = useContactMutation({
    onSuccess: (data) => {
      setSubmitMessage("Thank you! Your message has been sent successfully.");
      showToast("Your message has been sent successfully!", "success");
      setSelectedCountryCode("");
      // Reset form on success
      if (formActionsRef.current.resetForm) {
        formActionsRef.current.resetForm();
      }
      if (formActionsRef.current.setSubmitting) {
        formActionsRef.current.setSubmitting(false);
      }
    },
    onError: (error) => {
      setSubmitMessage(
        "Sorry, there was an error sending your message. Please try again."
      );
      showToast(
        error.message || "Error sending message. Please try again.",
        "error"
      );
      if (formActionsRef.current.setSubmitting) {
        formActionsRef.current.setSubmitting(false);
      }
    },
  });

  // Show toast notification
  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitMessage("");
    // Store form actions for use in mutation callbacks
    formActionsRef.current = { resetForm, setSubmitting };

    // Prepare submission data (exclude countryCode as it's not needed by API)
    const submissionData = {
      name: values.name,
      email: values.email,
      company: values.company,
      mobile: values.mobile,
      country: values.country,
      city: values.city,
      message: values.message,
    };

    submitContact(submissionData);
    // setSubmitting will be set to false in the mutation callback
  };

  const breadcrumbs = [{ label: "Home", link: "/" }, { label: "Contact Us" }];

  return (
    <div className="">
      <EmersonHeader />

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <Breadcrumb title="Contact Us" breadcrumbs={breadcrumbs} />

      {/* contact single area start */}
      <div className="rts-contact-area rts-section-gap">
        <div className="container">
          <div className="row g-5">
            {/* single contact area */}
            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12">
              <div className="single-contact-one-inner">
                {/* <div className="thumbnail">
                  <img src="assets/images/contact/c1.jpg" alt="" />
                </div> */}
                <div className="content">
                  <div className="icone">
                    <img src="assets/images/contact/shape/01.svg" alt="" />
                  </div>
                  <div className="info">
                    <span>Call Us</span>
                    <Link to={"tel:+91-422-23419872"}>
                      <h5>+91-422-23419872</h5>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* single contact area end */}
            {/* single contact area */}
            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12">
              <div className="single-contact-one-inner">
                {/* <div className="thumbnail">
                  <img src="assets/images/contact/c2.jpg" alt="" />
                </div> */}
                <div className="content">
                  <div className="icone">
                    <img src="assets/images/contact/shape/02.svg" alt="" />
                  </div>
                  <div className="info">
                    <span>Email Us</span>
                    <Link
                      to={"mailto:enquiry@sembian.net"}
                      style={{
                        display: "block",
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      <h5
                        style={{
                          wordBreak: "break-word",
                          fontSize: "clamp(18px, 2vw, 24px)",
                          lineHeight: "1.4",
                          maxWidth: "100%",
                          overflowWrap: "break-word",
                        }}
                      >
                        enquiry@sembian.net
                      </h5>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* single contact area end */}
            {/* single contact area */}
            <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12">
              <div className="single-contact-one-inner">
                {/* <div className="thumbnail">
                  <img src="assets/images/contact/c3.jpg" alt="" />
                </div> */}
                <div className="content">
                  <div className="icone">
                    <img src="assets/images/contact/shape/03.svg" alt="" />
                  </div>
                  <div className="info">
                    <span>Our Location</span>
                    <Link
                      to={"https://maps.app.goo.gl/oGR2FcLUzqcZvCDc9"}
                      target="_blank"
                    >
                      <h5>Coimbatore</h5>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* single contact area end */}
          </div>
        </div>
      </div>
      {/* conact single area end */}
      {/* bizup map area start */}
      {/* <div className="rts-contact-map-area">
        <div className="contaciner-fluid">
          <div className="row">
            <div className="col-12">
              <div className="contact-map-area-fluid">
                <iframe
                  className="contact-map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3915.0792743089687!2d77.07267867575474!3d11.107469652998855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f919fb4a96b9%3A0xd7ebee98491fe298!2sSembian%20Research%20%26%20Engineering%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1763725424337!5m2!1sen!2sin"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </div> */}
      {/* bizup map area end */}

      {/* conact us form fluid start */}
      <div className="rts-contact-form-area">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="rts-contact-fluid rts-section-gap">
                <div className="rts-title-area contact-fluid text-center mb--50">
                  <p className="pre-title">Get In Touch</p>
                  <h2 className="title">Needs Help? Let's Get in Touch</h2>
                </div>
                <div className="form-wrapper">
                  <div id="form-messages">
                    {submitMessage && (
                      <div
                        className={`form-message ${
                          submitMessage.includes("error") ? "error" : "success"
                        }`}
                      >
                        {submitMessage}
                      </div>
                    )}
                  </div>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={createValidationSchema(
                      selectedCountryCode
                    )}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                  >
                    {({ isSubmitting, errors, touched }) => (
                      <Form
                        id="contact-form"
                        style={{ maxWidth: "800px", margin: "0 auto" }}
                      >
                        <div
                          className="name-email"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                            marginBottom: "20px",
                          }}
                        >
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <Field
                              type="text"
                              name="name"
                              placeholder="Your Name *"
                              className={
                                errors.name && touched.name ? "error" : ""
                              }
                              style={{
                                width: "100%",
                                padding: "16px 20px",
                                fontSize: "16px",
                                border: `2px solid ${
                                  errors.name && touched.name
                                    ? "#ff0000"
                                    : "#e0e0e0"
                                }`,
                                borderRadius: "8px",
                                transition: "all 0.3s ease",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#ef1414";
                                e.target.style.boxShadow =
                                  "0 0 0 3px rgba(239, 20, 20, 0.1)";
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor =
                                  errors.name && touched.name
                                    ? "#ff0000"
                                    : "#e0e0e0";
                                e.target.style.boxShadow = "none";
                              }}
                            />
                            <ErrorMessage
                              name="name"
                              component="span"
                              className="error-message"
                              style={{
                                display: "contents",
                                color: "#ff0000",
                                fontSize: "13px",
                                marginTop: "6px",
                                fontWeight: "500",
                              }}
                            />
                          </div>
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <Field
                              type="email"
                              name="email"
                              placeholder="Business Email *"
                              className={
                                errors.email && touched.email ? "error" : ""
                              }
                              style={{
                                width: "100%",
                                padding: "16px 20px",
                                fontSize: "16px",
                                border: `2px solid ${
                                  errors.email && touched.email
                                    ? "#ff0000"
                                    : "#e0e0e0"
                                }`,
                                borderRadius: "8px",
                                transition: "all 0.3s ease",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#ef1414";
                                e.target.style.boxShadow =
                                  "0 0 0 3px rgba(239, 20, 20, 0.1)";
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor =
                                  errors.email && touched.email
                                    ? "#ff0000"
                                    : "#e0e0e0";
                                e.target.style.boxShadow = "none";
                              }}
                            />
                            <ErrorMessage
                              name="email"
                              component="span"
                              className="error-message"
                              style={{
                                display: "contents",
                                color: "#ff0000",
                                fontSize: "13px",
                                marginTop: "6px",
                                fontWeight: "500",
                              }}
                            />
                          </div>
                        </div>
                        <div
                          className="name-email"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                            marginBottom: "20px",
                          }}
                        >
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <Field
                              type="text"
                              name="company"
                              placeholder="Company Name *"
                              className={
                                errors.company && touched.company ? "error" : ""
                              }
                              style={{
                                width: "100%",
                                padding: "16px 20px",
                                fontSize: "16px",
                                border: `2px solid ${
                                  errors.company && touched.company
                                    ? "#ff0000"
                                    : "#e0e0e0"
                                }`,
                                borderRadius: "8px",
                                transition: "all 0.3s ease",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#ef1414";
                                e.target.style.boxShadow =
                                  "0 0 0 3px rgba(239, 20, 20, 0.1)";
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor =
                                  errors.company && touched.company
                                    ? "#ff0000"
                                    : "#e0e0e0";
                                e.target.style.boxShadow = "none";
                              }}
                            />
                            <ErrorMessage
                              name="company"
                              component="span"
                              className="error-message"
                              style={{
                                display: "contents",
                                color: "#ff0000",
                                fontSize: "13px",
                                marginTop: "6px",
                                fontWeight: "500",
                              }}
                            />
                          </div>
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <div style={{ position: "relative" }}>
                              {selectedCountryCode && (
                                <span
                                  style={{
                                    position: "absolute",
                                    left: "20px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    zIndex: 2,
                                    color: "#ef1414",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    backgroundColor: "#fff5f5",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    border: "1px solid #ffe0e0",
                                  }}
                                >
                                  +
                                  {countryOptions.find(
                                    (c) => c.isoCode === selectedCountryCode
                                  )?.phonecode || ""}
                                </span>
                              )}
                              <Field
                                type="tel"
                                name="mobile"
                                placeholder={
                                  selectedCountryCode
                                    ? "Enter phone number *"
                                    : "Select country first"
                                }
                                className={
                                  errors.mobile && touched.mobile ? "error" : ""
                                }
                                style={{
                                  width: "100%",
                                  padding: "16px 20px",
                                  paddingLeft: selectedCountryCode
                                    ? "90px"
                                    : "20px",
                                  fontSize: "16px",
                                  border: `2px solid ${
                                    errors.mobile && touched.mobile
                                      ? "#ff0000"
                                      : "#e0e0e0"
                                  }`,
                                  borderRadius: "8px",
                                  transition: "all 0.3s ease",
                                  backgroundColor: !selectedCountryCode
                                    ? "#f5f5f5"
                                    : "#fff",
                                  outline: "none",
                                  cursor: !selectedCountryCode
                                    ? "not-allowed"
                                    : "text",
                                }}
                                disabled={!selectedCountryCode}
                                onFocus={(e) => {
                                  if (selectedCountryCode) {
                                    e.target.style.borderColor = "#ef1414";
                                    e.target.style.boxShadow =
                                      "0 0 0 3px rgba(239, 20, 20, 0.1)";
                                  }
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor =
                                    errors.mobile && touched.mobile
                                      ? "#ff0000"
                                      : "#e0e0e0";
                                  e.target.style.boxShadow = "none";
                                }}
                              />
                            </div>
                            <ErrorMessage
                              name="mobile"
                              component="span"
                              className="error-message"
                              style={{
                                display: "contents",
                                color: "#ff0000",
                                fontSize: "13px",
                                marginTop: "6px",
                                fontWeight: "500",
                              }}
                            />
                            {selectedCountryCode &&
                              !errors.mobile &&
                              !touched.mobile && (
                                <small
                                  style={{
                                    display: "contents",
                                    marginTop: "6px",
                                    color: "#666",
                                    fontSize: "12px",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Enter number without country code
                                </small>
                              )}
                          </div>
                        </div>
                        <div
                          className="name-email"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                            marginBottom: "20px",
                          }}
                        >
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <Field name="country">
                              {({ field, form }) => (
                                <>
                                  <Select
                                    {...field}
                                    options={countryOptions}
                                    placeholder="Select Country *"
                                    isSearchable
                                    isClearable
                                    styles={{
                                      ...customSelectStyles,
                                      control: (base, state) => ({
                                        ...customSelectStyles.control(
                                          base,
                                          state
                                        ),
                                        borderColor:
                                          errors.country && touched.country
                                            ? "#ff0000"
                                            : state.isFocused
                                            ? "#ef1414"
                                            : "#e0e0e0",
                                        borderWidth: "2px",
                                      }),
                                    }}
                                    onChange={(option) => {
                                      form.setFieldValue(
                                        "country",
                                        option ? option.value : ""
                                      );
                                      form.setFieldValue(
                                        "countryCode",
                                        option ? option.isoCode : ""
                                      );
                                      setSelectedCountryCode(
                                        option ? option.isoCode : ""
                                      );
                                      form.setFieldValue("city", ""); // Reset city when country changes
                                      form.setFieldValue("mobile", ""); // Reset mobile when country changes
                                      // Re-validate mobile field when country changes
                                      form.validateField("mobile");
                                    }}
                                    value={
                                      countryOptions.find(
                                        (option) => option.value === field.value
                                      ) || null
                                    }
                                  />
                                  {errors.country && touched.country && (
                                    <span
                                      className="error-message"
                                      style={{
                                        display: "contents",
                                        color: "#ff0000",
                                        fontSize: "13px",
                                        marginTop: "6px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {errors.country}
                                    </span>
                                  )}
                                </>
                              )}
                            </Field>
                          </div>
                          <div
                            className="input-wrapper"
                            style={{ position: "relative" }}
                          >
                            <Field name="city">
                              {({ field, form }) => (
                                <>
                                  <Select
                                    {...field}
                                    options={cityOptions}
                                    placeholder="Select City *"
                                    isSearchable
                                    isClearable
                                    isDisabled={!selectedCountryCode}
                                    styles={{
                                      ...customSelectStyles,
                                      control: (base, state) => ({
                                        ...customSelectStyles.control(
                                          base,
                                          state
                                        ),
                                        borderColor:
                                          errors.city && touched.city
                                            ? "#ff0000"
                                            : state.isFocused
                                            ? "#ef1414"
                                            : "#e0e0e0",
                                        backgroundColor: !selectedCountryCode
                                          ? "#f5f5f5"
                                          : "#fff",
                                        borderWidth: "2px",
                                      }),
                                    }}
                                    onChange={(option) => {
                                      form.setFieldValue(
                                        "city",
                                        option ? option.value : ""
                                      );
                                    }}
                                    value={
                                      cityOptions.find(
                                        (option) => option.value === field.value
                                      ) || null
                                    }
                                  />
                                  {errors.city && touched.city && (
                                    <span
                                      className="error-message"
                                      style={{
                                        display: "contents",
                                        color: "#ff0000",
                                        fontSize: "13px",
                                        marginTop: "6px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {errors.city}
                                    </span>
                                  )}
                                </>
                              )}
                            </Field>
                          </div>
                        </div>
                        <div
                          className="textarea-wrapper"
                          style={{ marginBottom: "30px" }}
                        >
                          <Field
                            as="textarea"
                            name="message"
                            placeholder="Type Your Message *"
                            rows="6"
                            className={
                              errors.message && touched.message ? "error" : ""
                            }
                            style={{
                              width: "100%",
                              padding: "16px 20px",
                              fontSize: "16px",
                              border: `2px solid ${
                                errors.message && touched.message
                                  ? "#ff0000"
                                  : "#e0e0e0"
                              }`,
                              borderRadius: "8px",
                              transition: "all 0.3s ease",
                              backgroundColor: "#fff",
                              outline: "none",
                              resize: "vertical",
                              minHeight: "140px",
                              fontFamily: "inherit",
                              lineHeight: "1.6",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#ef1414";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(239, 20, 20, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor =
                                errors.message && touched.message
                                  ? "#ff0000"
                                  : "#e0e0e0";
                              e.target.style.boxShadow = "none";
                            }}
                          />
                          <ErrorMessage
                            name="message"
                            component="span"
                            className="error-message"
                            style={{
                              display: "contents",
                              color: "#ff0000",
                              fontSize: "13px",
                              marginTop: "6px",
                              fontWeight: "500",
                            }}
                          />
                        </div>
                        <div style={{ textAlign: "center", marginTop: "10px" }}>
                          <button
                            type="submit"
                            className="rts-btn btn-primary"
                            disabled={isSubmitting || isPending}
                            style={{
                              padding: "16px 48px",
                              fontSize: "16px",
                              fontWeight: "600",
                              backgroundColor:
                                isSubmitting || isPending ? "#ccc" : "#ef1414",
                              color: "#fff",
                              border: "none",
                              borderRadius: "8px",
                              cursor:
                                isSubmitting || isPending
                                  ? "not-allowed"
                                  : "pointer",
                              transition: "all 0.3s ease",
                              boxShadow:
                                isSubmitting || isPending
                                  ? "none"
                                  : "0 4px 12px rgba(239, 20, 20, 0.3)",
                              minWidth: "200px",
                              textTransform: "none",
                              letterSpacing: "0.5px",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting && !isPending) {
                                e.target.style.backgroundColor = "#d01212";
                                e.target.style.transform = "translateY(-2px)";
                                e.target.style.boxShadow =
                                  "0 6px 16px rgba(239, 20, 20, 0.4)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting && !isPending) {
                                e.target.style.backgroundColor = "#ef1414";
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow =
                                  "0 4px 12px rgba(239, 20, 20, 0.3)";
                              }
                            }}
                          >
                            {isSubmitting || isPending ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    border: "2px solid #fff",
                                    borderTopColor: "transparent",
                                    borderRadius: "50%",
                                    animation: "spin 0.8s linear infinite",
                                    display: "inline-block",
                                  }}
                                ></span>
                                Sending...
                              </span>
                            ) : (
                              "Send Message"
                            )}
                          </button>
                        </div>
                        <style>{`
                          @keyframes spin {
                            to { transform: rotate(360deg); }
                          }
                          @media (max-width: 768px) {
                            #contact-form .name-email {
                              grid-template-columns: 1fr !important;
                            }
                          }
                        `}</style>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* conact us form fluid end */}

      {/* World Map Section */}
      <div
        className="rts-contact-map-area"
        style={{ padding: "120px 0 50px 0", backgroundColor: "#f8f9fa" }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="rts-title-area contact-fluid-map text-center mb--50">
                <p className="pre-title">Our Global Reach</p>
                <h2 className="title">Connecting the World</h2>
              </div>
              <div style={{ maxWidth: "100%", margin: "0 auto" }}>
                <WorldMap
                  dots={[
                    {
                      start: { lat: -5.0168, lng: 78.9558 }, // Coimbatore, India
                      end: { lat: 40.7128, lng: -74.006 }, // New York, USA
                    },
                    {
                      start: { lat: -5.0168, lng: 78.9558 }, // Coimbatore, India
                      end: { lat: 51.5074, lng: -0.1278 }, // London, UK
                    },
                    {
                      start: { lat: -5.0168, lng: 78.9558 }, // Coimbatore, India
                      end: { lat: 35.6762, lng: 139.6503 }, // Tokyo, Japan
                    },
                    {
                      start: { lat: -5.0168, lng: 78.9558 }, // Coimbatore, India
                      end: { lat: -33.8688, lng: 145.2093 }, // Sydney, Australia
                    },
                  ]}
                  lineColor="#ef1414"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* World Map Section end */}

      <FooterTwo />
    </div>
  );
}

export default ContactUs;
